import { Link, Navigate, useParams } from "react-router-dom";
import { serviceBySlug, serviceOffers } from "@/lib/service-offers";

export default function ServiceOfferPage() {
    const { slug } = useParams();
    const offer = serviceBySlug(slug);

    if (!offer) return <Navigate to="/services" replace />;

    const related = serviceOffers.filter((item) => item.slug !== offer.slug);

    return (
        <main data-testid={`service-${offer.slug}`} className="bg-white text-[#0a0a0b]">
            <section className="bg-lavender px-5 pb-14 pt-36 sm:pb-16 sm:pt-44">
                <div className="mx-auto max-w-5xl">
                    <Link to="/services" className="text-sm font-semibold text-[#5b5b66] hover:text-[var(--brand)]">
                        ← All services
                    </Link>
                    <p className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--brand)]">{offer.kicker}</p>
                    <h1 className="mt-2 font-display text-4xl font-extrabold leading-tight sm:text-6xl">{offer.title}</h1>
                    <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[#5b5b66] sm:text-lg">{offer.summary}</p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to="/#contact" className="inline-flex rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(43,80,255,0.35)]">
                            Book a free consultation
                        </Link>
                        <Link to="/pricing" className="inline-flex rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#0a0a0b] ring-1 ring-black/10">
                            See pricing
                        </Link>
                    </div>
                </div>
            </section>

            <section className="px-5 py-16">
                <div className="mx-auto max-w-5xl">
                    <h2 className="font-display text-3xl font-extrabold">{offer.featuresTitle}</h2>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {offer.features.map((feature) => (
                            <article key={feature.title} className="rounded-3xl bg-[#f6f7fb] p-5 ring-1 ring-black/5">
                                <h3 className="font-display text-lg font-bold">{feature.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-[#5b5b66]">{feature.body}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {offer.compare ? (
                <section className="px-5 pb-4">
                    <div className="mx-auto max-w-5xl">
                        <h2 className="font-display text-3xl font-extrabold">{offer.compare.title}</h2>
                        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#5b5b66]">{offer.compare.intro}</p>
                        <div className="mt-6 overflow-x-auto rounded-3xl ring-1 ring-black/5">
                            <table className="w-full min-w-[640px] text-left text-sm">
                                <thead className="bg-[#0a0a0b] text-white">
                                    <tr>
                                        {offer.compare.columns.map((column) => (
                                            <th key={column} className="px-4 py-3 font-semibold">{column}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {offer.compare.rows.map((row) => (
                                        <tr key={row[0]} className="border-t border-black/5 bg-white">
                                            {row.map((cell) => (
                                                <td key={cell} className="px-4 py-3 align-top leading-relaxed text-[#3a3a44]">{cell}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            ) : null}

            <section className="px-5 py-16">
                <div className="mx-auto grid max-w-5xl gap-4 rounded-3xl bg-[#0a0a0b] p-6 text-white sm:grid-cols-[1.2fr_1fr] sm:p-8">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/60">Transparent pricing</p>
                        <h2 className="mt-2 font-display text-2xl font-extrabold">This service typically costs</h2>
                        <p className="mt-2 font-display text-3xl font-extrabold text-white">$1,997 – $12,000+</p>
                        <p className="mt-3 text-sm leading-relaxed text-white/70">{offer.priceFrom}</p>
                    </div>
                    <div>
                        <p className="text-sm font-semibold">Unlike open-ended consulting</p>
                        <ul className="mt-3 space-y-2 text-sm text-white/80">
                            <li>No hidden fees</li>
                            <li>All-inclusive packages</li>
                            <li>Fixed-price guarantee</li>
                        </ul>
                        <Link to="/pricing" className="mt-5 inline-flex rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#0a0a0b]">
                            View detailed pricing
                        </Link>
                    </div>
                </div>
            </section>

            <section className="bg-[#f6f7fb] px-5 py-16">
                <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-2">
                    <div>
                        <h2 className="font-display text-2xl font-extrabold">What you get</h2>
                        <ul className="mt-4 space-y-2">
                            {offer.gets.map((item) => (
                                <li key={item} className="text-sm leading-relaxed text-[#3a3a44]">• {item}</li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <h2 className="font-display text-2xl font-extrabold">{offer.stepsTitle}</h2>
                        <ol className="mt-4 space-y-4">
                            {offer.steps.map((step, index) => (
                                <li key={step.title}>
                                    <p className="text-xs font-semibold text-[var(--brand)]">0{index + 1}</p>
                                    <p className="font-semibold">{step.title}</p>
                                    <p className="mt-1 text-sm leading-relaxed text-[#5b5b66]">{step.body}</p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            <section className="px-5 py-16">
                <div className="mx-auto grid max-w-5xl gap-4 md:grid-cols-3">
                    <Info title="What we need from you" items={offer.need} />
                    <Info title={offer.notThis ? "What the audit is not" : "What stays with a person"} items={offer.notThis || offer.stays || []} />
                    <Info title="Limits to expect" items={offer.limits} />
                </div>
                {offer.note ? <p className="mx-auto mt-8 max-w-5xl text-sm leading-relaxed text-[#5b5b66]">{offer.note}</p> : null}
                {offer.workLink ? (
                    <p className="mx-auto mt-4 max-w-5xl text-sm">
                        <Link to="/#work" className="font-semibold text-[var(--brand)]">See the websites we have built →</Link>
                    </p>
                ) : null}
            </section>

            <section className="bg-[#f6f7fb] px-5 py-16">
                <div className="mx-auto max-w-3xl">
                    <h2 className="font-display text-3xl font-extrabold">Questions</h2>
                    <div className="mt-6 space-y-3">
                        {offer.faq.map((item) => (
                            <div key={item.q} className="rounded-2xl bg-white p-5 ring-1 ring-black/5">
                                <p className="font-semibold">{item.q}</p>
                                <p className="mt-2 text-sm leading-relaxed text-[#5b5b66]">{item.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-5 py-16">
                <div className="mx-auto max-w-5xl">
                    <h2 className="font-display text-2xl font-extrabold">Related services</h2>
                    <div className="mt-5 flex flex-wrap gap-2">
                        {related.map((item) => (
                            <Link key={item.slug} to={`/services/${item.slug}`} className="rounded-full bg-[#f6f7fb] px-4 py-2 text-sm font-semibold ring-1 ring-black/5 hover:text-[var(--brand)]">
                                {item.menu}
                            </Link>
                        ))}
                        <Link to="/services/sales" className="rounded-full bg-[#f6f7fb] px-4 py-2 text-sm font-semibold ring-1 ring-black/5 hover:text-[var(--brand)]">
                            Sales Management
                        </Link>
                    </div>
                    <div className="mt-10 rounded-3xl bg-lavender p-6 sm:p-8">
                        <h2 className="font-display text-3xl font-extrabold">{offer.ctaTitle}</h2>
                        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#5b5b66]">{offer.ctaBody}</p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/pricing" className="inline-flex rounded-full bg-white px-5 py-2.5 text-sm font-semibold ring-1 ring-black/10">See pricing</Link>
                            <Link to="/#contact" className="inline-flex rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(43,80,255,0.28)]">Get started</Link>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}

function Info({ title, items }) {
    if (!items.length) return null;
    return (
        <div className="rounded-3xl bg-[#f6f7fb] p-5 ring-1 ring-black/5">
            <h2 className="font-display text-lg font-bold">{title}</h2>
            <ul className="mt-3 space-y-2">
                {items.map((item) => (
                    <li key={item} className="text-sm leading-relaxed text-[#5b5b66]">{item}</li>
                ))}
            </ul>
        </div>
    );
}
